export type AchievementId = 'mirror_maker' | 'branching_collective' | 'no_spectators'

export type AchievementForm = 'nft' | 'fleeting'

export type AchievementUnlock = {
  id: AchievementId
  title: string
  description: string
  imageUrl: string
  unlockedAt: number
  form: AchievementForm
  mintedOnChain: boolean
}

export type AchievementDef = {
  id: AchievementId
  title: string
  description: string
  imageUrl: string
  congrats: string
}

export const ACHIEVEMENT_DEFS: Record<AchievementId, AchievementDef> = {
  mirror_maker: {
    id: 'mirror_maker',
    title: 'Mirror Maker',
    description: 'Mint your first memory',
    imageUrl: '/badges/mirror-maker.png',
    congrats: 'Congratulations — you have been awarded the Mirror Maker achievement.',
  },
  branching_collective: {
    id: 'branching_collective',
    title: 'Branching the Collective',
    description: 'Mint seven memories',
    imageUrl: '/badges/branching-collective.svg',
    congrats: 'Congratulations — you have been awarded Branching the Collective.',
  },
  no_spectators: {
    id: 'no_spectators',
    title: 'No Spectators',
    description: 'Engage with ten memories from other creators',
    imageUrl: '/badges/no-spectators.png',
    congrats: 'Congratulations — you have been awarded No Spectators.',
  },
}

const ACHIEVEMENT_ORDER: AchievementId[] = [
  'mirror_maker',
  'branching_collective',
  'no_spectators',
]

const storageKey = (wallet: string) => `memoria:achievements:${wallet.toLowerCase()}`
const mintCountKey = (wallet: string) => `memoria:achievementMints:${wallet.toLowerCase()}`
const interactionKey = (wallet: string) => `memoria:achievementInteractions:${wallet.toLowerCase()}`

type StoredAchievements = Record<AchievementId, AchievementUnlock>

const readStore = (wallet: string): StoredAchievements => {
  try {
    const raw = localStorage.getItem(storageKey(wallet))
    if (!raw) return {} as StoredAchievements
    return JSON.parse(raw) as StoredAchievements
  } catch {
    return {} as StoredAchievements
  }
}

const writeStore = (wallet: string, store: StoredAchievements) => {
  try {
    localStorage.setItem(storageKey(wallet), JSON.stringify(store))
  } catch {
    /* ignore */
  }
}

export const readAchievementUnlocks = (wallet: string): AchievementUnlock[] => {
  const store = readStore(wallet)
  return ACHIEVEMENT_ORDER.map((id) => store[id]).filter(Boolean)
}

export const getMintCountForAchievements = (wallet: string): number => {
  try {
    const raw = localStorage.getItem(mintCountKey(wallet))
    const n = raw ? Number(raw) : 0
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
  } catch {
    return 0
  }
}

export const incrementMintCountForAchievements = (wallet: string): number => {
  const next = getMintCountForAchievements(wallet) + 1
  try {
    localStorage.setItem(mintCountKey(wallet), String(next))
  } catch {
    /* ignore */
  }
  return next
}

export const readInteractionKeys = (wallet: string): Set<string> => {
  try {
    const raw = localStorage.getItem(interactionKey(wallet))
    if (!raw) return new Set()
    const arr = JSON.parse(raw) as string[]
    return new Set(Array.isArray(arr) ? arr : [])
  } catch {
    return new Set()
  }
}

export const recordMemoryInteraction = (
  wallet: string,
  memoryKey: string
): number => {
  const set = readInteractionKeys(wallet)
  if (set.has(memoryKey)) return set.size
  set.add(memoryKey)
  try {
    localStorage.setItem(interactionKey(wallet), JSON.stringify([...set]))
  } catch {
    /* ignore */
  }
  return set.size
}

const thresholdFor = (id: AchievementId, mintCount: number, interactionCount: number): boolean => {
  if (id === 'mirror_maker') return mintCount >= 1
  if (id === 'branching_collective') return mintCount >= 7
  return interactionCount >= 10
}

export type AchievementUnlockListener = (unlock: AchievementUnlock) => void

const listeners = new Set<AchievementUnlockListener>()

export const subscribeAchievementUnlocks = (fn: AchievementUnlockListener) => {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

const emitUnlock = (unlock: AchievementUnlock) => {
  listeners.forEach((fn) => fn(unlock))
}

export const acceptAchievement = (
  wallet: string,
  id: AchievementId,
  form: AchievementForm
): AchievementUnlock | null => {
  const store = readStore(wallet)
  const existing = store[id]
  if (!existing) return null
  const next: AchievementUnlock = {
    ...existing,
    form,
    mintedOnChain: form === 'nft',
  }
  store[id] = next
  writeStore(wallet, store)
  return next
}

export const evaluateAchievements = (wallet: string): AchievementUnlock | null => {
  if (!wallet.startsWith('0x')) return null
  const mintCount = getMintCountForAchievements(wallet)
  const interactionCount = readInteractionKeys(wallet).size
  const store = readStore(wallet)

  for (const id of ACHIEVEMENT_ORDER) {
    if (store[id]) continue
    if (!thresholdFor(id, mintCount, interactionCount)) continue
    const def = ACHIEVEMENT_DEFS[id]
    const unlock: AchievementUnlock = {
      id,
      title: def.title,
      description: def.description,
      imageUrl: def.imageUrl,
      unlockedAt: Date.now(),
      form: 'fleeting',
      mintedOnChain: false,
    }
    store[id] = unlock
    writeStore(wallet, store)
    emitUnlock(unlock)
    return unlock
  }
  return null
}

export const evaluateAfterMint = (wallet: string): AchievementUnlock | null => {
  incrementMintCountForAchievements(wallet)
  return evaluateAchievements(wallet)
}

export const evaluateAfterInteraction = (
  wallet: string,
  memoryKey: string,
  isOwnMemory: boolean
): AchievementUnlock | null => {
  if (isOwnMemory || !wallet.startsWith('0x')) return null
  recordMemoryInteraction(wallet, memoryKey)
  return evaluateAchievements(wallet)
}
